/* eslint-disable valid-jsdoc */
import {
	normalizeToMasked,
	formatUS,
	inputSlotsBeforeCaret,
	caretPosFromDigitIndex,
	clampCaretMinPrefix,
	PREFIX_LEN
} from '@/helper/phoneMaskUS';

/**
 * Установить маску на элемент и (опц.) сообщить наружу.
 * @param {HTMLInputElement} el - инпут.
 * @param {string} raw - сырая строка (вставка/значение).
 * @param {Object} opts - опции.
 * @param {boolean} [opts.emit] - эмитить наружу (через колбэк).
 */
function applyMasked(el, raw, { emit = true } = {}) {
	const { formatted, digits } = normalizeToMasked(String(raw || ''));
	el.value = formatted;
	el._pmu.masked = formatted;
	el._pmu.digits = digits;

	if (emit && typeof el._pmu.onChange === 'function') {
		el._pmu.onChange(formatted, digits);
	}
}

/**
 * Корректно поставить каретку в позицию "по индексам цифр".
 * @param {HTMLInputElement} el - инпут.
 * @param {number} digitIndex - индекс цифро-слота.
 */
function setCaretByDigitIndex(el, digitIndex) {
	const pos = Math.max(PREFIX_LEN, caretPosFromDigitIndex(el._pmu.masked, digitIndex));
	el.setSelectionRange(pos, pos);
}

/**
 * Фокус: снимаем readonly и не даём поставить каретку левее "+1 (".
 * @param {FocusEvent} evt - событие фокуса.
 */
function onFocus(evt) {
	const el = evt.target;
	if (!el) return;

	if (!el._pmu.readonly) el.removeAttribute('readonly');
	// защита префикса
	const pos = clampCaretMinPrefix(el.selectionStart || 0);
	el.setSelectionRange(pos, pos);
}

/**
 * Клик мышью: если кликнули не по цифре — переносим каретку в конец (по цифрам).
 * @param {MouseEvent} evt - событие mouseup.
 */
function onMouseUp(evt) {
	const el = evt.target;
	if (!el) return;

	// даём браузеру выставить каретку, затем корректируем
	setTimeout(() => {
		const s = el._pmu.masked || '';
		const pos = el.selectionStart || 0;
		const left = s[pos - 1] || '';
		const at = s[pos] || '';
		const nearDigit = /\d/.test(left) || /\d/.test(at);

		if (!nearDigit) {
			setCaretByDigitIndex(el, el._pmu.digits.length);
		}
	}, 0);
}

/**
 * Нормализуем ввод (включая автозаполнение).
 * @param {Event} evt - input/change событие.
 */
function onNormalize(evt) {
	const el = evt.target;
	if (!el) return;

	const before = inputSlotsBeforeCaret(el.value, el.selectionStart);
	applyMasked(el, el.value, { emit: true });

	// вернуть каретку максимально корректно
	setTimeout(() => setCaretByDigitIndex(el, before), 0);
}

/**
 * Вставка из буфера обмена.
 * @param {ClipboardEvent} evt - событие paste.
 */
function onPaste(evt) {
	const el = evt.target;
	if (!el) return;

	evt.preventDefault();
	const pasted = evt.clipboardData.getData('text') || '';
	applyMasked(el, pasted, { emit: true });

	// каретку — в конец по цифрам
	setTimeout(() => setCaretByDigitIndex(el, el._pmu.digits.length), 0);
}

/**
 * Полностью управляемая клавиатура: только цифры, mac delete=backspace,
 * защита префикса, запрет 0/1 как первой цифры area code.
 * @param {KeyboardEvent} evt - keydown событие.
 */
function onKeydown(evt) {
	const el = evt.target;
	if (!el) return;

	const key = evt.key;
	const code = evt.keyCode;
	const isMacOS = /Mac/i.test(navigator.platform || navigator.userAgent || '');

	// навигация и пр.
	const allowedNav = new Set([ 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Escape', 'Enter' ]);
	const isShortcut = (evt.ctrlKey || evt.metaKey) && /[acvxyz]/i.test(key);
	if (allowedNav.has(key) || isShortcut) return;

	// deleteMode
	let del = null; // 'backspace' | 'delete' | null
	if (key === 'Backspace' || code === 8) del = 'backspace';
	else if (key === 'Delete' || code === 46) del = isMacOS ? 'backspace' : 'delete';

	const start = el.selectionStart;
	const end = el.selectionEnd;

	// защита префикса
	if (del && start < PREFIX_LEN) {
		evt.preventDefault();
		const fix = Math.max(PREFIX_LEN, end || 0);
		el.setSelectionRange(fix, fix);
		return;
	}

	// удаление
	if (del) {
		evt.preventDefault();

		// если диапазон
		if (start !== end) {
			const leftIdx = inputSlotsBeforeCaret(el._pmu.masked, start);
			const rightIdx = inputSlotsBeforeCaret(el._pmu.masked, end);

			let d = el._pmu.digits;
			const toRemove = Math.max(0, Math.min(d.length, rightIdx) - Math.min(d.length, leftIdx));
			if (toRemove > 0) {
				d = d.slice(0, leftIdx) + d.slice(leftIdx + toRemove);
				el._pmu.digits = d;
				el._pmu.masked = formatUS(d);
				el.value = el._pmu.masked;

				setTimeout(() => setCaretByDigitIndex(el, leftIdx), 0);
				if (typeof el._pmu.onChange === 'function') el._pmu.onChange(el._pmu.masked, d);
			} else {
				el.setSelectionRange(Math.max(PREFIX_LEN, start), Math.max(PREFIX_LEN, start));
			}
			return;
		}

		// одиночное удаление
		const idx = inputSlotsBeforeCaret(el._pmu.masked, start);
		const removeAt = del === 'backspace' ? idx - 1 : idx;
		if (removeAt >= 0 && removeAt < el._pmu.digits.length) {
			const d = el._pmu.digits.slice(0, removeAt) + el._pmu.digits.slice(removeAt + 1);
			el._pmu.digits = d;
			el._pmu.masked = formatUS(d);
			el.value = el._pmu.masked;

			setTimeout(() => {
				const caretIdx = del === 'backspace' ? Math.max(0, idx - 1) : idx;
				setCaretByDigitIndex(el, caretIdx);
			}, 0);

			if (typeof el._pmu.onChange === 'function') el._pmu.onChange(el._pmu.masked, d);
		}
		return;
	}

	// вставка цифры
	if (/^\d$/.test(key)) {
		evt.preventDefault();
		let d = el._pmu.digits;
		if (d.length >= 10) return;

		const leftIdx = inputSlotsBeforeCaret(el._pmu.masked, start);
		const rightIdx = inputSlotsBeforeCaret(el._pmu.masked, end);

		// запрет 0/1 как первой цифры area code
		if (leftIdx === 0 && (key === '0' || key === '1')) return;

		if (start !== end && rightIdx > leftIdx) {
			const span = Math.min(d.length, rightIdx) - Math.min(d.length, leftIdx);
			d = d.slice(0, leftIdx) + d.slice(leftIdx + Math.max(0, span));
			if (d.length < 10) d = d.slice(0, leftIdx) + key + d.slice(leftIdx);
			el._pmu.digits = d;
			el._pmu.masked = formatUS(d);
			el.value = el._pmu.masked;

			setTimeout(() => setCaretByDigitIndex(el, leftIdx + 1), 0);
			if (typeof el._pmu.onChange === 'function') el._pmu.onChange(el._pmu.masked, d);
			return;
		}

		// без выделения — вставка одной цифры
		const idx = leftIdx;
		el._pmu.digits = d.slice(0, idx) + key + d.slice(idx);
		el._pmu.masked = formatUS(el._pmu.digits);
		el.value = el._pmu.masked;

		setTimeout(() => setCaretByDigitIndex(el, idx + 1), 0);
		if (typeof el._pmu.onChange === 'function') el._pmu.onChange(el._pmu.masked, el._pmu.digits);
		return;
	}

	// все прочие печатные символы — блокируем
	if (key && key.length === 1 && !evt.ctrlKey && !evt.metaKey && !evt.altKey) {
		evt.preventDefault();
	}
}

export default {
	/**
	 * @param {HTMLInputElement} el - элемент.
	 * @param {{value?: { onChange?: Function, initial?: string, readonly?: boolean }}} binding - биндинг.
	 */
	inserted(el, binding) {
		el._pmu = {
			masked: '',
			digits: '',
			onChange: binding && binding.value && binding.value.onChange,
			readonly: !!(binding && binding.value && binding.value.readonly)
		};

		// начальное значение (из props/value)
		const initial = binding && binding.value && binding.value.initial != null
			? String(binding.value.initial)
			: (el.value || '');

		// применяем без эмита
		applyMasked(el, initial, { emit: false });

		// слушатели
		el.addEventListener('focus', onFocus);
		el.addEventListener('keydown', onKeydown);
		el.addEventListener('input', onNormalize);
		el.addEventListener('change', onNormalize);
		el.addEventListener('paste', onPaste);
		el.addEventListener('mouseup', onMouseUp);

		// подстраховка на автозаполнение браузером
		setTimeout(() => applyMasked(el, el.value, { emit: false }), 120);
	},

	/**
	 * Обновления от родителя (v-bind:value / props).
	 */
	componentUpdated(el, binding, vnode) {
		const domVal = vnode && vnode.data && vnode.data.domProps && 'value' in vnode.data.domProps
			? vnode.data.domProps.value
			: el.value;

		const external = binding && binding.value && binding.value.initial != null
			? String(binding.value.initial)
			: domVal;

		// если снаружи поменяли — переформатируем без эмита
		if (String(external || '') !== el._pmu.masked) {
			const caretDigitIdx = inputSlotsBeforeCaret(el._pmu.masked, el.selectionStart || 0);
			applyMasked(el, external, { emit: false });
			setTimeout(() => setCaretByDigitIndex(el, caretDigitIdx), 0);
		}
	},

	unbind(el) {
		el.removeEventListener('focus', onFocus);
		el.removeEventListener('keydown', onKeydown);
		el.removeEventListener('input', onNormalize);
		el.removeEventListener('change', onNormalize);
		el.removeEventListener('paste', onPaste);
		el.removeEventListener('mouseup', onMouseUp);
		delete el._pmu;
	}
};
